import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  signal,
  WritableSignal,
} from '@angular/core';
import { form, minLength, required } from '@angular/forms/signals';
import { MatDialog } from '@angular/material/dialog';
import {
  FileId,
  PropertyAddMediaDto,
  PropertyCharacteristicsDto,
  PropertyId,
  PropertyPhotoDto,
} from '@workspace/data-types';
import { AbstractSignalFormComponent, PhotoUploadModalAuraComponent } from '@workspace/ng-aura';
import { ApiService } from '@workspace/ng-http';
import { catchError, forkJoin, map, of, tap } from 'rxjs';
import { PropertyDataCleaner } from '../../../utils/property-data.cleaner';
import { PhotoGalleryComponent } from '../ui/photo-gallery/photo-gallery.component';
import { PhotoManageComponent } from '../ui/photo-manage/photo-manage.component';

export interface PropertyMediaFormModel {
  photos: PropertyPhotoDto[];
}

@Component({
  selector: 'etxe-property-photos-form',
  standalone: true,
  imports: [CommonModule, PhotoGalleryComponent, PhotoManageComponent],
  templateUrl: './photos-form.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PropertyPhotosFormComponent extends AbstractSignalFormComponent<
  PropertyAddMediaDto,
  PropertyMediaFormModel
> {
  private readonly api = inject(ApiService);
  private readonly dialog = inject(MatDialog);

  propertyId = input.required<PropertyId>();
  propertyCharacteristics = input.required<PropertyCharacteristicsDto>();
  isManageMode = signal(false);

  override model: WritableSignal<PropertyMediaFormModel> = signal<PropertyMediaFormModel>({
    photos: [],
  });

  override formTree = form(this.model, (b) => {
    required(b.photos);
    minLength(b.photos, 1);
  });

  photos = computed(() => {
    const rawPhotos = this.model().photos || [];
    return [...rawPhotos].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  });

  openUploadModal() {
    const ref = this.dialog.open(PhotoUploadModalAuraComponent, {
      width: '100%',
      maxWidth: '600px',
      panelClass: 'bg-transparent',
    });

    ref.afterClosed().subscribe((files: File[] | null) => {
      if (files && files.length > 0) {
        this.uploadFiles(files);
        this.toggleManageMode();
      }
    });
  }

  toggleManageMode() {
    this.isManageMode.set(!this.isManageMode());
  }

  updatePhotos(updated: PropertyPhotoDto[]) {
    this.model.update((prev) => ({ ...prev, photos: updated }));
  }

  private uploadFiles(files: File[]) {
    if (!files.length) return;

    const uploadRequests = files.map((file) =>
      this.api.business().etxe().properties().owner().uploadPhoto(this.propertyId(), file),
    );

    forkJoin(uploadRequests)
      .pipe(
        map((results) => {
          const currentPhotos = this.photos();
          return (results || [])
            .filter((r) => Boolean(r?.url))
            .map((result, idx) => {
              const resObj = result as unknown as Record<string, unknown>;
              return {
                file: resObj['_id'] || resObj['id'],
                url: result?.url ?? '',
                order: currentPhotos.length + idx,
              } as PropertyPhotoDto;
            });
        }),
        tap((newPhotos) => {
          if (newPhotos.length > 0) {
            this.model.update((prev) => ({ ...prev, photos: [...this.photos(), ...newPhotos] }));
          }
        }),
        catchError((err) => {
          console.error('Failed to upload some files', err);
          return of([]);
        }),
      )
      .subscribe();
  }

  protected override buildModelFromDto(dto: PropertyAddMediaDto): PropertyMediaFormModel {
    return {
      photos: dto?.photos ?? [],
    };
  }

  protected override buildDtoFromModel(model: PropertyMediaFormModel): PropertyAddMediaDto {
    const rawDto: PropertyAddMediaDto = {
      ...(this.dto() || ({} as PropertyAddMediaDto)),
      photos: model.photos,
    };

    // Prevent saving populated nested objects to the database
    if (Array.isArray(rawDto.photos)) {
      rawDto.photos = rawDto.photos.map((photo) => {
        const cleanedPhoto = { ...photo };

        // Strip the populated 'file' object and keep only the reference string ID
        if (cleanedPhoto.file && typeof cleanedPhoto.file === 'object') {
          const fileObj = cleanedPhoto.file as unknown as Record<string, unknown>;
          cleanedPhoto.file = (fileObj['_id'] || fileObj['id']) as unknown as FileId;
        }

        return cleanedPhoto;
      });
    }

    return PropertyDataCleaner.deepClean(rawDto) as PropertyAddMediaDto;
  }
}
